import type { AsyncStorageLike } from "@assistant-ui/core/react";
import type { ThreadHistoryAdapter } from "@assistant-ui/core";
import { api } from "@/lib/api";

const cloudAsyncStorage: AsyncStorageLike = {
  async getItem(key) {
    const result = await api<{ value: string | null }>(
      `/api/chat-storage?key=${encodeURIComponent(key)}`,
    );
    return result.value;
  },
  async setItem(key, value) {
    await api<{ ok: true }>("/api/chat-storage", {
      method: "PUT",
      body: JSON.stringify({ key, value }),
    });
  },
  async removeItem(key) {
    await api<{ ok: true }>(
      `/api/chat-storage?key=${encodeURIComponent(key)}`,
      { method: "DELETE" },
    );
  },
};

type StoredRepository = {
  headId?: string | null;
  messages: Array<{ message: { id: string }; parentId: string | null }>;
};

export function withMessageDeletion(
  history: ThreadHistoryAdapter,
  storage: AsyncStorageLike,
  prefix: string,
  getRemoteId: () => Promise<string>,
): ThreadHistoryAdapter {
  const adapted = Object.assign(
    Object.create(Object.getPrototypeOf(history)) as ThreadHistoryAdapter,
    history,
  );
  adapted.delete = async (items) => {
    if (items.length === 0) return;
    const remoteId = await getRemoteId();
    const key = `${prefix}messages:${remoteId}`;
    const raw = await storage.getItem(key);
    if (!raw) return;

    let repository: StoredRepository;
    try {
      const parsed = JSON.parse(raw) as StoredRepository;
      if (!Array.isArray(parsed.messages)) return;
      repository = parsed;
    } catch {
      return;
    }

    const deletedIds = new Set(items.map((item) => item.message.id));
    if (!repository.messages.some((item) => deletedIds.has(item.message.id)))
      return;
    const parents = new Map(
      repository.messages.map((item) => [item.message.id, item.parentId]),
    );
    const messages = repository.messages.flatMap((item) => {
      if (deletedIds.has(item.message.id)) return [];
      let parentId = item.parentId;
      const visited = new Set<string>();
      while (parentId !== null && deletedIds.has(parentId)) {
        if (visited.has(parentId)) {
          parentId = null;
          break;
        }
        visited.add(parentId);
        parentId = parents.get(parentId) ?? null;
      }
      return [{ ...item, parentId }];
    });

    let headId = repository.headId;
    if (headId && deletedIds.has(headId)) {
      const keptParents = new Set(messages.map((item) => item.parentId));
      headId = null;
      for (let index = messages.length - 1; index >= 0; index -= 1) {
        const id = messages[index]!.message.id;
        if (!keptParents.has(id)) {
          headId = id;
          break;
        }
      }
    }

    await storage.setItem(
      key,
      JSON.stringify({ ...repository, messages, headId }),
    );
  };
  return adapted;
}

export function getChatStorage(): AsyncStorageLike {
  return cloudAsyncStorage;
}

export function accountStorageScope(owner: string): string {
  return `huopan-lite:${owner}:`;
}
